import { ChevronDown, ChevronsUpDown, ChevronUp, Columns3 } from 'lucide-react';
import { useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { HERD_COLUMNS, type HerdColumnDef } from '@/lib/herd-columns';
import type { HerdAnimal, Role } from '@/lib/schemas';
import { cn } from '@/lib/utils';

interface HerdTableProps {
  animals: HerdAnimal[];
  total: number;
  isLoading: boolean;
  role: Role;
  visibleColumns: Array<keyof HerdAnimal>;
  onColumnVisibilityChange: (columns: Array<keyof HerdAnimal>) => void;
  sortBy: string | null;
  sortDir: 'asc' | 'desc';
  onSort: (column: string) => void;
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  onCellEdit: (animalId: string, field: string, value: unknown) => void;
  onAnimalClick: (animalId: string) => void;
  onLoadMore: () => void;
  hasMore: boolean;
}

// --- Column Chooser ---

function ColumnChooser({
  visibleColumns,
  onColumnVisibilityChange,
}: {
  visibleColumns: Array<keyof HerdAnimal>;
  onColumnVisibilityChange: (columns: Array<keyof HerdAnimal>) => void;
}) {
  function toggle(key: keyof HerdAnimal) {
    const isVisible = visibleColumns.includes(key);
    if (isVisible) {
      onColumnVisibilityChange(visibleColumns.filter((k) => k !== key));
    } else {
      onColumnVisibilityChange([...visibleColumns, key]);
    }
  }

  return (
    <Popover>
      <PopoverTrigger>
        <Button variant="outline" size="sm" className="gap-2">
          <Columns3 size={14} />
          Columns
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-48">
        <div className="space-y-2">
          <p className="text-xs font-semibold text-ndd-400 mb-2">Visible Columns</p>
          {HERD_COLUMNS.map((col) => (
            // biome-ignore lint/a11y/noLabelWithoutControl: label wraps Checkbox component
            <label key={col.key} className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={visibleColumns.includes(col.key)}
                onCheckedChange={() => toggle(col.key)}
              />
              <span className="text-sm text-white">{col.label}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// --- Inline Edit Cell ---

function InlineEditCell({
  col,
  value,
  onSave,
}: {
  col: HerdColumnDef;
  value: unknown;
  onSave: (value: unknown) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const displayValue = col.format
    ? col.format(value, {} as HerdAnimal)
    : value != null
      ? String(value)
      : '—';

  function startEdit() {
    setDraft(value != null ? String(value) : '');
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function commit() {
    setEditing(false);
    const typed = col.editType === 'number' ? Number(draft) : draft;
    onSave(typed);
  }

  function cancel() {
    setEditing(false);
  }

  if (editing) {
    if (col.editType === 'select' && col.editOptions) {
      return (
        <Select
          value={draft}
          onValueChange={(v) => {
            setDraft(v);
            setEditing(false);
            onSave(v);
          }}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {col.editOptions.map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    return (
      <Input
        ref={inputRef}
        type={col.editType === 'number' ? 'number' : col.editType === 'date' ? 'date' : 'text'}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') cancel();
        }}
        onBlur={commit}
        className="h-8 text-xs py-0"
      />
    );
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: inline edit cell, keyboard via input
    // biome-ignore lint/a11y/useKeyWithClickEvents: inline edit cell, keyboard via input
    <span
      onClick={startEdit}
      className="block min-h-[24px] cursor-text hover:bg-ndd-800/40 rounded px-1 py-0.5 text-sm"
      title="Click to edit"
    >
      {displayValue}
    </span>
  );
}

// --- Status Badge ---

const STATUS_COLORS: Record<string, string> = {
  Milking: 'bg-ndd-600',
  Dry: 'bg-amber-600',
  'Close-up': 'bg-purple-600',
  Heifer: 'bg-blue-600',
  Calf: 'bg-cyan-600',
  Dead: 'bg-gray-600',
  Sold: 'bg-gray-500',
};

// --- Main Table ---

export function HerdTable({
  animals,
  total,
  isLoading,
  role,
  visibleColumns,
  onColumnVisibilityChange,
  sortBy,
  sortDir,
  onSort,
  selectedIds,
  onSelectionChange,
  onCellEdit,
  onAnimalClick,
  onLoadMore,
  hasMore,
}: HerdTableProps) {
  const isFarmer = role === 'farmer';
  const cols = HERD_COLUMNS.filter((c) => visibleColumns.includes(c.key));

  const allSelected = animals.length > 0 && animals.every((a) => selectedIds.has(a.animal_id));

  function toggleAll() {
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(animals.map((a) => a.animal_id)));
    }
  }

  function toggleRow(id: string) {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onSelectionChange(next);
  }

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-ndd-400">{total} animals</p>
        <ColumnChooser
          visibleColumns={visibleColumns}
          onColumnVisibilityChange={onColumnVisibilityChange}
        />
      </div>

      <div className="rounded-lg border border-ndd-800 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              {isFarmer && (
                <TableHead className="w-8">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleAll}
                    aria-label="Select all"
                  />
                </TableHead>
              )}
              {cols.map((col) => (
                <TableHead
                  key={col.key}
                  style={col.width ? { width: col.width } : undefined}
                  className={col.sortable ? 'cursor-pointer select-none' : ''}
                  onClick={col.sortable ? () => onSort(col.key) : undefined}
                >
                  <span className="flex items-center gap-1">
                    {col.label}
                    {col.sortable &&
                      (sortBy === col.key ? (
                        sortDir === 'asc' ? (
                          <ChevronUp size={12} />
                        ) : (
                          <ChevronDown size={12} />
                        )
                      ) : (
                        <ChevronsUpDown size={12} className="opacity-40" />
                      ))}
                  </span>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={cols.length + (isFarmer ? 1 : 0)}
                  className="py-8 text-center text-ndd-400"
                >
                  Loading…
                </TableCell>
              </TableRow>
            ) : animals.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={cols.length + (isFarmer ? 1 : 0)}
                  className="py-8 text-center text-ndd-400"
                >
                  No animals found
                </TableCell>
              </TableRow>
            ) : (
              animals.map((animal) => (
                <TableRow
                  key={animal.animal_id}
                  data-selected={selectedIds.has(animal.animal_id)}
                  className="cursor-pointer"
                >
                  {isFarmer && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedIds.has(animal.animal_id)}
                        onCheckedChange={() => toggleRow(animal.animal_id)}
                      />
                    </TableCell>
                  )}
                  {cols.map((col) => {
                    const rawValue = animal[col.key];
                    return (
                      <TableCell
                        key={col.key}
                        onClick={
                          col.editable && isFarmer
                            ? undefined
                            : () => onAnimalClick(animal.animal_id)
                        }
                      >
                        {col.key === 'status' ? (
                          isFarmer && col.editable ? (
                            <InlineEditCell
                              col={col}
                              value={rawValue}
                              onSave={(v) => onCellEdit(animal.animal_id, col.key, v)}
                            />
                          ) : (
                            <Badge
                              className={cn(
                                'text-white',
                                STATUS_COLORS[animal.status] ?? 'bg-gray-600'
                              )}
                            >
                              {animal.status}
                            </Badge>
                          )
                        ) : isFarmer && col.editable ? (
                          <InlineEditCell
                            col={col}
                            value={rawValue}
                            onSave={(v) => onCellEdit(animal.animal_id, col.key, v)}
                          />
                        ) : (
                          <span className="text-sm">
                            {col.format
                              ? col.format(rawValue, animal)
                              : rawValue != null
                                ? String(rawValue)
                                : '—'}
                          </span>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {hasMore && !isLoading && (
        <button
          type="button"
          onClick={onLoadMore}
          className="w-full rounded-lg bg-ndd-800 py-2 text-sm font-medium hover:bg-ndd-700"
        >
          Load more
        </button>
      )}
    </div>
  );
}
